/* eslint-disable react/prop-types */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {useEffect, useState} from 'react';
import Icon from '../Icon/Icon';
import NewFileDropCard from '../NewFileDropCard/NewFileDropCard';
// import NewCanvasCard from '../NewCanvasCard/NewCanvasCard';
import getConfig from '../../../config';
import './AppHomeTab.scss';
import {AppHomeDialog} from './AppHomeDialog';
import {AppHomeCard} from './AppHomeCard';
import AppHomeBanner from './AppHomeBanner';
import {AppHomeUCSData, SophiaBannerItem} from '../../containers/AppHome/types';
import ScrollCarousel from '../ScrollCarousel/ScrollCarousel';

type Props = {
	recent?: any;
	leftRailOpen: boolean;
	intl: any;
	createSpacesCallback: Function;
	ucsAppHomeData: AppHomeUCSData;
	bannerData: SophiaBannerItem;
};

const AppHomeContent: React.FC<Props> = ({
	recent,
	ucsAppHomeData,
	bannerData,
	leftRailOpen,
	intl,
	createSpacesCallback,
}) => {
	const [selectedTab, setSelectedTab] = useState('home');
	const cardLoadDelay = 4000;

	const handleTabClick = newtab => (e) => {
		e.preventDefault();
		setSelectedTab(newtab);
	};

	const canvasSpinner = !!window.cchome.util.hasFeature('canvas_spinner');
	const comingSoonDisabled = !!window.cchome.util.hasFeature('apphomes_disable_soon');

	useEffect(() => {
		if (ucsAppHomeData.appContextType === 'photoshopweb' && !document.getElementById('ps-preload')) {
			// Triggers the preload of PS Web via an invisible iframe
			const preloadIframe = document.createElement('iframe');
			preloadIframe.id = 'ps-preload';
			preloadIframe.style.display = 'none';
			const photoshopWebUrl = getConfig('PHOTOSHOP_WEB');
			preloadIframe.src = `${photoshopWebUrl}preload.html`;
			document.body.appendChild(preloadIframe);
			window.cchome.util.logger.log('PS Web App Home Tab', 'Preloaded PS Web iframe');
		}
	}, []);

	// UCS DATA RELATED
	const renderUCSCards = (cards, addViewAllCard) => {
		const renderedCards: Array<false | JSX.Element> = [];

		for (const card of cards) {
			// todo, if more apps will use new file drop card whether the app link should be in the card itself or passed in
			const newFileLink = ucsAppHomeData.appContextType === 'illustratorweb' ? `${getConfig('ILLUSTRATOR_WEB')}new` : `${getConfig('PHOTOSHOP_WEB')}new`;
			const newCanvasLink = `${getConfig('FRED_WEB')}new`;
			switch (card.contentType) {
				case 'newFile': {
					renderedCards.push(
						<NewFileDropCard
							createNewFileLink={newFileLink}
							iconImgSrc={card.image}
							cardTitle={card.title.long}
							i={card.id}
							intl={intl}
							appContextType={ucsAppHomeData.appContextType}
						/>,
					);
					break;
				}
				case 'learnCard': {
					renderedCards.push(
						<AppHomeCard
							key={card.id}
							text={card.title.long}
							link={card.url}
							imgUrl={card.image || ''}
							type={'tutorial'}
							title={card.subtitle.long}
							loadDelay={cardLoadDelay}
							comingSoon={!comingSoonDisabled}
							spinner={canvasSpinner}
							intl={intl}
							apphomeData={{}}
						/>,
					);
					break;
				}
				case 'newCanvas':
					renderedCards.push(
						<AppHomeDialog
							key={card.id}
							text={card.title.long}
							link={newCanvasLink}
							imgUrl={card.image}
							type={'dialog'}
							intl={intl}
							createSpacesCallback={createSpacesCallback}
						/>,
					);
					break;
				case 'fredStarterTemplate':
					renderedCards.push(
						<AppHomeCard
							key={card.id}
							text={card.title.long}
							link={''}
							imgUrl={card.image || ''}
							type={'card'}
							title={card.subtitle.long}
							loadDelay={cardLoadDelay}
							comingSoon={!comingSoonDisabled}
							spinner={canvasSpinner}
							intl={intl}
							apphomeData={{}}
						/>,
					);
					break;
			}
		}

		// If we have the learn tab, we add the View All card that takes us to the learn tab
		if (addViewAllCard) {
			renderedCards.push(
				<div className='apphome-card view-all'>
					{/* TODO Replace img with actual icon and change static 'learn' onClick */}
					<div className='view-all-img-container'>
						<img
							className='view-all-img'
							onClick={handleTabClick('learn')}
							aria-label='View all Learn topics'
							role='link'
							alt='view all icon'
							src='/assets/common/view-all.svg'
						/>
					</div>
					<div className='view-all-text' >{intl.formatMessage({id: 'apphomes.view.all'})}</div>
				</div>,
			);
		}
		return (<> {renderedCards} </>);
	};


	// For now, rendering tab content depending on selectedTab and static comparison
	const renderTabContent = (tab) => {
		if (tab.context !== selectedTab) {
			return null;
		}
		else if (tab.context === 'home') {
			return (
				<div key={`${tab.context}-body`} className={'action-row'}>
					{/* {tab.links?.map(renderCard)}
										{tab.cards?.map(renderUCSCards)}
										{renderUCSCards(tab.cards)} */}
					{<ScrollCarousel scrollDistance={480}>{renderUCSCards(tab.cards, true)}</ScrollCarousel>}
				</div>
			);
		}
		return (
			<div key={`${tab.context}-body`} className={'action-row'}>
				{renderUCSCards(tab.cards, false)}
			</div>
		);
	};

	// UCS DATA RELATED
	const renderTabs = (item) => {
		const tabsUCS = item.tabs;

		return (
			<div className='start-action-container' role='region' aria-label={selectedTab[0].toUpperCase() + selectedTab.slice(1)}>
				{tabsUCS.length > 0
					&& (tabsUCS.length > 1 ? (
						<>
							<ul className='tab-list'>
								{tabsUCS.map(tab =>
									(
										<li
											key={tab.context}
											className={`tab-selector${selectedTab === tab.context ? ' selected' : ''}`}
											onClick={handleTabClick(tab.context)}
										>
											<Icon name={tab.icon} />
											<h2>{tab.title}</h2>
										</li>
									),
								)}
							</ul>
							<div className='tab-body'>
								{tabsUCS.map(tab => (renderTabContent(tab)))}
							</div>
						</>
					) : (
						<>
							<div className='tab-body'>
								<div key={`${tabsUCS[0].context}-body`} className={'action-row'}>
									{/* TODO: FIX TAB SELECTED STATE CSS HERE */}
									{/* {tabsUCS[0].cards?.map(renderUCSCards)} */}
									{<ScrollCarousel scrollDistance={480}>{renderUCSCards(tabsUCS[0].cards, false)}</ScrollCarousel>}
								</div>
							</div>
						</>
					))}
			</div>
		);
	};

	// UCS DATA RELATED
	let banner;
	const components: Array<false | JSX.Element> = [];
	for (const item of ucsAppHomeData.items) {
		const componentType = item.componentType;
		switch (componentType) {
			case 'banner':
				banner = (
					<AppHomeBanner intl={intl} bannerData={bannerData} appContextType={ucsAppHomeData.appContextType} />
				);
				break;
			case 'tabs':
				components.push(renderTabs(item));
				break;
			case 'recents':
				components.push(<div className='recent-container' role='region' aria-label='Recent files'>{recent}</div>);
				break;
		}
	}

	return (
		<div className={`app-home-tab${leftRailOpen ? ' leftnav-open' : ''}`}>
			{banner}
			<div className='bottom-container'>{components}</div>
		</div>
	);
};

export default AppHomeContent;
